/**
 * Multi-Product Tabs Controller
 *
 * Handles tab switching between products on the PDP using
 * Shopify's Section Rendering API for AJAX content updates.
 */

(function() {
  'use strict';

  const SELECTORS = {
    tabsContainer: '[data-multi-product-tabs]',
    tab: '[data-product-handle]',
    // Elements to update when switching products
    gallery: '.PBFCM-PDP-product-gallery, .product__media-wrapper',
    title: '.product__title',
    productInfo: '.product__info-wrapper',
    priceWrapper: '[id^="price-"]',
    skuWrapper: '[id^="sku-"]',
    productForm: '[id^="product-form-"]',
    variantPicker: 'variant-selects, variant-radios',
    buyButtons: '.product-form__buttons, .buy-buttons',
    description: '.product__description',
    accordion: '.accordion',
    inventoryStatus: '.inventory-status, [data-inventory-status]',
    // PBFCM-specific selectors (purchase content, upsells, config)
    purchaseContent: '.product-purchase-content',
    frequencySelector: '.PBFCM-PDP-frequency-selector',
    upsellContainer: '.PBFCM-PDP-upsell-container',
    cartSummary: '.PBFCM-PDP-cart-summary',
    // Benefit accordion stats (metafield-driven, product-specific)
    benefitAccordion: '.pdp-benefit-accordion-stats'
  };

  class MultiProductTabs {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.tabs = container.querySelectorAll(SELECTORS.tab);
      this.isLoading = false;

      this.bindEvents();
    }

    bindEvents() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', (e) => this.handleTabClick(e));
      });
    }

    handleTabClick(event) {
      const tab = event.currentTarget;
      const productHandle = tab.dataset.productHandle;
      const productUrl = tab.dataset.productUrl;

      // Don't reload if clicking the active tab
      if (tab.classList.contains('active') || this.isLoading) {
        return;
      }

      this.switchProduct(productUrl, productHandle, tab);
    }

    async switchProduct(productUrl, productHandle, clickedTab) {
      this.setLoading(true);

      // Preserve straight-to-checkout setting (page-level, not product-level)
      const stcEl = document.getElementById('PBFCM-straight-to-checkout');
      const originalStraightToCheckout = stcEl ? stcEl.value : null;

      try {
        const sectionHtml = await this.fetchProductSection(productUrl);
        this.updatePageContent(sectionHtml, productHandle);
        this.updateActiveTab(clickedTab);
        this.reinitializeComponents(originalStraightToCheckout);
      } catch (error) {
        console.error('Multi-Product Tabs: Error switching product', error);
      } finally {
        this.setLoading(false);
      }
    }

    async fetchProductSection(productUrl) {
      const url = `${productUrl}?section_id=${this.sectionId}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch product section: ${response.status}`);
      }

      return await response.text();
    }

    updatePageContent(html, productHandle) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector(`#shopify-section-${this.sectionId}`);

      if (!newSection) {
        console.error('Multi-Product Tabs: Could not find section in response');
        return;
      }

      const currentSection = document.querySelector(`#shopify-section-${this.sectionId}`);

      if (!currentSection) {
        console.error('Multi-Product Tabs: Could not find current section');
        return;
      }

      // Update product gallery
      this.replaceElement(currentSection, newSection, SELECTORS.gallery);

      // Update product title
      this.replaceElement(currentSection, newSection, SELECTORS.title);

      // Update price
      this.replaceElement(currentSection, newSection, SELECTORS.priceWrapper);

      // Update SKU
      this.replaceElement(currentSection, newSection, SELECTORS.skuWrapper);

      // Update product form
      this.replaceElement(currentSection, newSection, SELECTORS.productForm);

      // Update variant picker
      this.replaceElement(currentSection, newSection, SELECTORS.variantPicker);

      // Update buy buttons
      this.replaceElement(currentSection, newSection, SELECTORS.buyButtons);

      // Update description (single element)
      this.replaceElement(currentSection, newSection, SELECTORS.description);

      // Update ALL accordion tabs by index — metafield content lives inside these
      // and there are multiple per page (Supplement Facts, Ingredients, etc.)
      this.replaceAllElements(currentSection, newSection, SELECTORS.accordion);

      // Update benefit accordion stats (metaobject-driven, product-specific).
      // The Section Rendering API does not return metaobject-reference metafields when
      // the section is on a custom page template but fetched at a /products/ URL.
      // We use a server-side pre-rendered cache instead — see #mpt-bas-cache-{sectionId}.
      this.updateBenefitAccordionFromCache(productHandle, currentSection, newSection);

      // Update inventory status
      this.replaceElement(currentSection, newSection, SELECTORS.inventoryStatus);

      // Update PBFCM-specific elements
      this.replaceElement(currentSection, newSection, SELECTORS.purchaseContent);
      this.replaceElement(currentSection, newSection, SELECTORS.frequencySelector);
      this.replaceElement(currentSection, newSection, SELECTORS.upsellContainer);
      this.replaceElement(currentSection, newSection, SELECTORS.cartSummary);

      // Fallback: update product info wrapper if gallery wasn't found
      const galleryUpdated = currentSection.querySelector(SELECTORS.gallery);
      if (!galleryUpdated) {
        this.replaceElement(currentSection, newSection, SELECTORS.productInfo);
      }
    }

    updateBenefitAccordionFromCache(productHandle, currentSection, newSection) {
      const cache = document.getElementById(`mpt-bas-cache-${this.sectionId}`);
      const currentBas = currentSection.querySelector(SELECTORS.benefitAccordion);

      if (!currentBas) return;

      currentBas.setAttribute('data-product-handle', productHandle);

      const cached = cache?.querySelector(`[data-cache-handle="${productHandle}"]`);
      const cachedHtml = cached?.innerHTML.trim() || '';

      if (cachedHtml) {
        currentBas.innerHTML = cached.innerHTML;
        currentBas.hidden = false;
        return;
      }

      // Fallback: section API response (works on product templates)
      const newBas = newSection?.querySelector(SELECTORS.benefitAccordion);
      const fetchedHtml = newBas?.innerHTML.trim() || '';

      if (fetchedHtml) {
        currentBas.innerHTML = newBas.innerHTML;
        currentBas.hidden = false;
        return;
      }

      currentBas.innerHTML = '';
      currentBas.hidden = true;
    }

    replaceElement(currentSection, newSection, selector) {
      const selectors = selector.split(',').map(s => s.trim());

      for (const sel of selectors) {
        const currentEl = currentSection.querySelector(sel);
        const newEl = newSection.querySelector(sel);

        if (currentEl && newEl) {
          currentEl.innerHTML = newEl.innerHTML;

          // Copy over data attributes and style attribute
          Array.from(newEl.attributes).forEach(attr => {
            if (attr.name.startsWith('data-') || attr.name === 'style') {
              currentEl.setAttribute(attr.name, attr.value);
            }
          });

          return true;
        }
      }

      return false;
    }

    // Replaces ALL matching elements by position — required for repeating blocks
    // like collapsible tabs where each block renders a separate .accordion element.
    replaceAllElements(currentSection, newSection, selector) {
      const currentEls = Array.from(currentSection.querySelectorAll(selector));
      const newEls = Array.from(newSection.querySelectorAll(selector));

      currentEls.forEach((currentEl, i) => {
        const newEl = newEls[i];
        if (!newEl) return;

        currentEl.innerHTML = newEl.innerHTML;
        Array.from(newEl.attributes).forEach(attr => {
          if (attr.name.startsWith('data-') || attr.name === 'style') {
            currentEl.setAttribute(attr.name, attr.value);
          }
        });
      });
    }

    // Replaces the entire element (outerHTML) rather than just its innerHTML.
    // Needed for elements like the benefit accordion where the root element
    // itself carries inline styles (CSS custom properties) that vary per product.
    replaceElementOuter(currentSection, newSection, selector) {
      const selectors = selector.split(',').map(s => s.trim());

      for (const sel of selectors) {
        const currentEl = currentSection.querySelector(sel);
        const newEl = newSection.querySelector(sel);

        if (currentEl && newEl) {
          currentEl.replaceWith(newEl.cloneNode(true));
          return true;
        }
      }

      return false;
    }

    updateActiveTab(clickedTab) {
      this.tabs.forEach(tab => {
        tab.classList.remove('active');
        tab.removeAttribute('aria-current');
      });

      clickedTab.classList.add('active');
      clickedTab.setAttribute('aria-current', 'true');
    }

    reinitializeComponents(originalStraightToCheckout = null) {
      // 1. Clear upsell state from previous product
      if (window.PBFCM_PDP && window.PBFCM_PDP.getState) {
        const currentState = window.PBFCM_PDP.getState();
        if (currentState.upsells) {
          Object.keys(currentState.upsells).forEach(key => {
            delete currentState.upsells[key];
          });
        }
      }

      // 2. Restore straight-to-checkout BEFORE init() reads it
      if (originalStraightToCheckout === 'true' || originalStraightToCheckout === '1') {
        const stcEl = document.getElementById('PBFCM-straight-to-checkout');
        if (stcEl) stcEl.value = originalStraightToCheckout;
      }

      // 3. Reinitialize PBFCM_PDP
      if (window.PBFCM_PDP && window.PBFCM_PDP.init) {
        window.PBFCM_PDP.init();
      }

      // 3b. Reinitialize gift progress
      if (window.PBFCM_PDP && window.PBFCM_PDP.giftProgress &&
          window.PBFCM_PDP.giftProgress.init) {
        window.PBFCM_PDP.giftProgress.init();
      }

      // 3c. Reinitialize product gallery
      if (window.PBFCM_PDP && window.PBFCM_PDP.productGallery &&
          window.PBFCM_PDP.productGallery.init) {
        window.PBFCM_PDP.productGallery.init();
      }

      // Reinitialize Shopify payment buttons
      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }

      // Reinitialize product form variant handling
      const variantSelects = document.querySelector(`#shopify-section-${this.sectionId} variant-selects`);
      if (variantSelects && typeof variantSelects.updateOptions === 'function') {
        variantSelects.updateOptions();
      }

      // Dispatch custom event for other scripts to listen to
      document.dispatchEvent(new CustomEvent('multi-product-tabs:switched', {
        detail: {
          sectionId: this.sectionId
        }
      }));

      // Reinitialize PBFCM gallery if available
      const gallery = document.querySelector(`#shopify-section-${this.sectionId} .PBFCM-PDP-product-gallery`);
      if (gallery && window.PBFCM && window.PBFCM.initGallery) {
        window.PBFCM.initGallery(gallery);
      }

      // Trigger Swiper reinitializations
      if (window.Swiper) {
        const swiperContainers = document.querySelectorAll(`#shopify-section-${this.sectionId} .swiper`);
        swiperContainers.forEach(container => {
          if (container.swiper) {
            container.swiper.update();
          }
        });
      }
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.container.classList.toggle('loading', isLoading);

      this.tabs.forEach(tab => {
        tab.disabled = isLoading;
      });
    }
  }

  // Initialize all tab containers on page load
  function init() {
    const tabContainers = document.querySelectorAll(SELECTORS.tabsContainer);
    tabContainers.forEach(container => {
      new MultiProductTabs(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize after section rendering (for theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    const tabContainers = event.target.querySelectorAll(SELECTORS.tabsContainer);
    tabContainers.forEach(container => {
      new MultiProductTabs(container);
    });
  });
})();